import { PRIMARY_TRAVELLER_FORM_ID } from 'constants/checkout'
import { arrayToObject } from 'lib/array/arrayUtils'
import { flattenFormFields } from 'lib/checkout/form'
import { getAttribute } from 'lib/object/objectUtils'

interface FormPayload {
  [fieldId: string]: string
}

interface Item {
  form: App.Checkout.TravellerForm
  schema: App.Checkout.TravellerFormSchema
}

interface Traveller {
  data: FormPayload
  index: number
  ticketId: string
}

interface TravellerInformationPayload {
  customer: FormPayload | null
  participants?: Array<Traveller>
}

function buildParticipantPayload(item: Item, formSchema: App.Checkout.FormSchema): FormPayload {
  const fieldKeys = Object.keys(flattenFormFields(item.schema.properties, formSchema))

  // Temporary until old phone field is removed from schemas
  if (!fieldKeys.includes('phone') && fieldKeys.includes('phonePrefix')) {
    fieldKeys.push('phone')
  }

  return fieldKeys.reduce<{ [key: string]: string }>((participant, property) => {
    const value = item.form && getAttribute(item.form.data, property)

    // saving "false" values for boolean fields
    if (value != undefined) {
      participant[property] = value
    }

    return participant
  }, {})
}

export function buildTravellerInfo(
  schema: App.Checkout.FormSchema,
  forms: Array<App.Checkout.TravellerForm>,
): TravellerInformationPayload {
  const formsMap = arrayToObject(forms, form => form.id, form => form)

  const primaryForm: App.Checkout.TravellerForm | undefined = formsMap[PRIMARY_TRAVELLER_FORM_ID]
  const customerItem: Item | undefined = primaryForm ? {
    form: primaryForm,
    schema: schema.properties[PRIMARY_TRAVELLER_FORM_ID],
  } : undefined

  const participantItems = Object.entries<App.Checkout.TravellerFormSchema>(schema.properties)
    .reduce((acc, [key, schema]) => {
      if (!schema.isParticipant) { return acc }
      return [...acc, { form: formsMap[key], schema }]
    }, [] as Array<Item>)

  const participantPayloadItems = participantItems.map(item => ({
    data: buildParticipantPayload(item, schema),
    index: item.schema.relatedItem?.index ?? 0,
    ticketId: item.schema.relatedItem?.id ?? '',
  }))

  return {
    customer: customerItem ? buildParticipantPayload(customerItem, schema) : null,
    ...(participantPayloadItems.length > 0 ? {
      participants: participantPayloadItems,
    } : {}),
  }
}
